import axiosInterceptor from '../utils/axios/axiosInterceptor';
import { projectRunBasePath } from './projectRunsApi';
import {
  CancelStep5IntentResponse,
  PerfReportDetailResponse,
  Step5IntentKind,
  Step5StatusResponse,
  TriggerPerfReportResponse,
} from '../types/step5';

const api = () => axiosInterceptor(true);

/**
 * Fetch the unified Step 5 status (scaling + 5a + 5b + intents) for a run. This is the
 * high-frequency poll target that drives the Step 5 page UI. Granular endpoints
 * (get_replays, get_perf_reports, get_perf_report) are still available for detail views
 * but the page can render from this single call.
 */
export async function getStep5Status(
  projectId: number,
  runId: number
): Promise<Step5StatusResponse> {
  const base = projectRunBasePath(projectId, runId);
  if (!base) {
    throw new Error('No project run selected. Cannot fetch Step 5 status.');
  }
  const response = await api().get<Step5StatusResponse>(`${base}/step5-status`);
  return response.data;
}

/**
 * POST /perf-reports — launches Step 5a (both 1x and 10x factors). Returns 202 with a
 * QUEUED status when scaling is still running or a replay is in flight; otherwise 200
 * with the freshly created PERFORMANCE_REPORT_PROCESSOR rows.
 */
export async function triggerPerfReport(
  projectId: number,
  runId: number
): Promise<TriggerPerfReportResponse> {
  const base = projectRunBasePath(projectId, runId);
  if (!base) {
    throw new Error('No project run selected. Cannot trigger perf report.');
  }
  const response = await api().post<TriggerPerfReportResponse>(
    `${base}/perf-reports`,
    {}
  );
  return response.data;
}

/**
 * GET /perf-reports/{perf_report_id} — detail with presigned URLs to each factor's
 * sequential / concurrent CSV output. The presigned URLs have a 15-minute TTL; the page
 * should re-fetch on each download click to mint fresh ones.
 */
export async function getPerfReport(
  projectId: number,
  runId: number,
  perfReportId: string
): Promise<PerfReportDetailResponse> {
  const base = projectRunBasePath(projectId, runId);
  if (!base) {
    throw new Error('No project run selected. Cannot fetch perf report.');
  }
  const response = await api().get<PerfReportDetailResponse>(
    `${base}/perf-reports/${perfReportId}`
  );
  return response.data;
}

/**
 * DELETE /step5-intents/{kind} — clear a QUEUED or REJECTED entry from STEP5_INTENT.
 * Used by the "Cancel" button on the queued banner and the "Dismiss" button on a
 * rejected banner.
 */
export async function cancelStep5Intent(
  projectId: number,
  runId: number,
  kind: Step5IntentKind
): Promise<CancelStep5IntentResponse> {
  const base = projectRunBasePath(projectId, runId);
  if (!base) {
    throw new Error('No project run selected. Cannot cancel intent.');
  }
  const response = await api().delete<CancelStep5IntentResponse>(
    `${base}/step5-intents/${kind}`
  );
  return response.data;
}
